import React, { useContext, useEffect, useState } from "react";
import { connect } from "react-redux";
import { makeStyles } from "@mui/styles";
import {
  Autocomplete,
  TextField,
  FormControlLabel,
  Button,
  Switch,
  Typography,
  useTheme,
  Box,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import {
  findDistinctPermissionTypes,
  findSecComponents,
  createSecComponent,
} from "../SecFunction";
import { handleError } from "../../../reducers/ErrorReducer";
import { notify } from "../../../reducers/NotifierReducer";
import { getAccessPermission } from "../../../reducers/PermissionsReducer";
import { MainPermissionContext } from ".";
import { permissionLabelOptions } from "../../../utils/constants";
import Autocomplete2 from "../../../components/Autocomplete2";

const useStyles = makeStyles(() => ({
  typeSwitch: {
    textAlign: "right",
  },
  fieldWidth: {
    maxWidth: 300,
    marginTop: 20,
  },
}));

function NewPermissions(props) {
  const classes = useStyles();
  const theme = useTheme();
  const [isNewType, setIsNewType] = useState(false);
  const [allTypes, setAllTypes] = useState([]);
  const [currentType, setCurrentType] = useState(null);
  const [label, setLabel] = useState("");
  const [customType, setCustomType] = useState("");
  const [labelError, setLabelError] = useState(null);
  const [labels, setLabels] = useState([]);

  useEffect(() => {
    fetchDistinctTypes();
    fetchSecComponents();
  }, []);

  const fetchDistinctTypes = async () => {
    const result = await findDistinctPermissionTypes();
    setAllTypes(result);
  };

  const fetchSecComponents = async () => {
    try {
      const components = await findSecComponents();
      setLabels(components.map((c) => c.Label.toLowerCase()));
    } catch (err) {
      props.handleError(err, "Error Fetching Components");
    }
  };

  const securityRolePermission = getAccessPermission(
    "security",
    "Roles And Permissions",
    "any"
  );
  const canSecurityRolePermissionAdd = securityRolePermission?.Create;

  const { fetchData } = useContext(MainPermissionContext);

  const handleSaveClick = async () => {
    const data = {
      Label: label,
      Type: isNewType ? customType : currentType.type,
    };
    try {
      await createSecComponent(data);
      props.notify("New Component Created", "success");
      setLabel("");
      setCustomType("");
      setCurrentType(null);
      setIsNewType(false);
      await fetchData();
    } catch (err) {
      props.handleError(err, "Error Creating New Component");
    }
  };

  const renderIsNewType = () => {
    const handleChange = () => {
      setIsNewType(!isNewType);
      setLabel("");
      setLabelError(null);
    };
    return (
      <div className="mt-4">
        <FormControlLabel
          control={<Switch checked={isNewType} onChange={handleChange} />}
          label="New Type"
          sx={{
            color:
              theme.palette.mode === "dark"
                ? theme.palette.common.white
                : "black",
          }}
        />
      </div>
    );
  };

  const renderTypes = () => {
    const handleChange = (e, newValue) => setCurrentType(newValue);
    return (
      <Autocomplete
        disablePortal
        options={allTypes}
        className={classes.fieldWidth}
        renderInput={(params) => (
          <TextField {...params} label="Types" variant="outlined" />
        )}
        onChange={handleChange}
        getOptionLabel={(option) => option.type}
        renderOption={(props, option) => <li {...props}>{option.type}</li>}
        size="small"
        value={currentType}
        ListboxProps={{ style: { maxHeight: "15rem" } }}
      />
    );
  };

  const renderLabel = () => {
    if (!isNewType) {
      const typeKey = currentType?.type?.toLowerCase();
      const optionsForTab = (permissionLabelOptions[typeKey] || []).map(
        (val) => ({ label: val })
      );

      return (
        <Autocomplete2
          className={classes.fieldWidth}
          options={optionsForTab}
          value={label ? { label } : null}
          getOptionLabel={(opt) =>
            typeof opt === "string" ? opt : opt?.label || ""
          }
          renderOption={(props, option) => (
            <li {...props}>
              {typeof option === "string" ? option : option.label}
            </li>
          )}
          onChange={(event, val) => {
            const newLabel = typeof val === "string" ? val : val?.label || "";
            setLabel(newLabel);

            if (newLabel && labels.includes(newLabel.toLowerCase())) {
              setLabelError("The value is already in use");
            } else {
              setLabelError(null);
            }
          }}
          onInputChange={(event, val, reason) => {
            if (reason === "input") setLabel(val);
          }}
          error={labelError}
        />
      );
    }

    return (
      <Box sx={{ marginTop: 2 }}>
        <TextField
          label="Label"
          variant="outlined"
          value={label}
          size="small"
          onChange={(e) => setLabel(e.target.value)}
          className={classes.fieldWidth}
          fullWidth
        />
      </Box>
    );
  };

  const renderCustomType = () => {
    return (
      <div>
        <TextField
          label="Custom Type"
          variant="outlined"
          value={customType}
          size="small"
          onChange={(e) => setCustomType(e.target.value)}
          className={classes.fieldWidth}
          fullWidth
        />
      </div>
    );
  };

  const renderSaveButton = () => {
    return (
      <div style={{ textAlign: "right" }}>
        <Button
          onClick={handleSaveClick}
          disabled={!label || (!isNewType && !currentType) || !!labelError}
          variant="contained"
          color="primary"
        >
          <SaveIcon className="mr-2" /> Save
        </Button>
      </div>
    );
  };

  return (
    <div>
      <Typography
        variant="h4"
        sx={{
          color:
            theme.palette.mode === "dark"
              ? theme.palette.common.white
              : "black",
        }}
      >
        Create New Permission
      </Typography>
      {canSecurityRolePermissionAdd && renderSaveButton()}
      {renderIsNewType()}
      {!isNewType && renderTypes()}
      {isNewType && renderCustomType()}
      {renderLabel()}
    </div>
  );
}

export default connect(null, { handleError, notify })(NewPermissions);
